import axios from 'axios';
import * as _ from 'lodash';

import { WORKFLOW_STEP_DEFAULT_HANDLER_INFOS, WORKFLOW_STEP_PERSON_LIMIT_INFINITE } from 'src/consts';
import { EWorkflowStepType, EWorkflowStepHandlerFromTarget, EWorkflowType, EWorkflowStepUserType } from 'src/enums';
import {
  IAssignWorkflowStepHandlerPersonsParams,
  IProject,
  IWorkflow,
  IWorkflowStep,
  IWorkflowStepAddData,
  IWorkflowTemplate,
  IUser,
  IHandlerPerson,
  IAudit,
  IScheduleInfo
} from 'src/models';

import toasterService from './toaster.service';
import userService from './user.service';

// 从目标对象的对应属性上取值，构成审批人信息
function getHandlerPersonsSync(
  targetObject: any, // 工作流节点审批人 取 属性值 的 目标对象
  handlerId: string, // 审批人 id 对应的属性
  handlerName: string // 审批人 name 对应的属性
): IHandlerPerson[] {
  const handlers: IHandlerPerson[] = [];
  if (!(targetObject && targetObject[handlerId])) {
    return [];
  }
  // 属性值是数组
  if (_.isArray(targetObject[handlerId])) {
    _.forEach(targetObject[handlerId], (value, index) => {
      handlers.push({ user_id: value, name: targetObject[handlerName][index] });
    });
  } else {
    // 属性值是单值
    handlers.push({ user_id: targetObject[handlerId], name: targetObject[handlerName] });
  }
  return handlers;
}

export default {
  // 获取工作流模板
  getWorkflowTemplateByType(type: EWorkflowType): Promise<IWorkflowTemplate> {
    return axios.get<IWorkflowTemplate>(`/api/audits/template/flows/${type}`).then(res => {
      return res.data;
    });
  },

  // 使用工作流模板属性来更新工作流各节点的可编辑属性
  updateWorkflowStepPropertiesFromTemplate(workflowType: EWorkflowType, workflow: IWorkflow): Promise<IWorkflow> {
    workflow = _.cloneDeep(workflow);
    return this.getWorkflowTemplateByType(workflowType).then(template => {
      const workflowTemplateSteps = template.data.steps;
      const workflowSteps = workflow.steps || [];
      _.forEach(workflowSteps, step => {
        const templateStep = _.find(workflowTemplateSteps, { name: step.name });
        // 模板上的节点
        if (templateStep) {
          step.enable_direct_end_flow = templateStep.enable_direct_end_flow;
          step.enable_step_score = templateStep.enable_step_score;
          step.score_options = templateStep.score_options;
          step.default_score = templateStep.default_score;
          step.enable_delete_step = templateStep.enable_delete_step;
          step.enable_delete_person = templateStep.enable_delete_person;
          step.disable_add_person = templateStep.disable_add_person;
          step.person_limit = templateStep.person_limit;
          step.person_limit_max = templateStep.person_limit_max;
          // 自定义节点
        } else {
          step.enable_direct_end_flow = false;
          step.enable_step_score = false;
          step.enable_delete_step = true;
          step.enable_delete_person = true;
          step.disable_add_person = false;
          step.person_limit = 1;
          step.person_limit_max = WORKFLOW_STEP_PERSON_LIMIT_INFINITE;
        }
      });
      return workflow;
    });
  },

  // 由模板生成工作流
  generateWorkflowFromTemplate(
    type: EWorkflowType,
    projects?: IProject[],   // 审批单上的项目列表
    audit?: IAudit
  ): Promise<IWorkflow> {
    return this.getWorkflowTemplateByType(type).then(template => {
      template.data.type = template.type; // type
      template.data.code = template.code; // code
      return this.assignWorkflowStepHandlerPersons({
        workflow: template.data,
        projectsGroup: {
          updatedProjects: projects,
          allDirtyProjects: projects
        },
        audit: audit
      });
    });
  },

  // 各工作流节点，根据模板配置，带出默认审批人
  // (开始节点带提交人由后端处理，前端不做处理)
  assignWorkflowStepHandlerPersons(params: IAssignWorkflowStepHandlerPersonsParams): Promise<IWorkflow> {
    return userService.getCurrentUser().then(currentUser => {
      const projectsGroup = _.cloneDeep(params.projectsGroup);
      const workflow = _.cloneDeep(params.workflow);
      const audit = _.cloneDeep(params.audit);
      if (!workflow?.steps?.length) {
        return workflow;
      }

      // 遍历工作流各节点
      _.forEach(workflow.steps, step => {
        if (EWorkflowStepType.Normal !== step.type) {
          return;
        }

        // 允许增删审批人，则追加；不允许，则全部替换
        const projects = step.enable_delete_person ? projectsGroup.updatedProjects : projectsGroup.allDirtyProjects;

        // 根据模板上的默认审批人类型，填充上项目的相关人员
        const handlerInfo = _.find(WORKFLOW_STEP_DEFAULT_HANDLER_INFOS, { userType: step.default_handler_role_id });
        if (!handlerInfo) {
          return;
        }
        let handlers: IHandlerPerson[] = [];
        switch (handlerInfo.target) {
          case EWorkflowStepHandlerFromTarget.Project:
            _.forEach(projects, project => {
              if (project && !project.created_by) {
                // 未下发的项目，项目创建人为当前用户
                project.created_by = currentUser.user_id;
                project.created_name = currentUser.name;
              }
              handlers = _.concat(handlers, getHandlerPersonsSync(project, handlerInfo.handlerId, handlerInfo.handlerName));
            });
            // 项目创建人：为空时（流程尚未提交），带出当前用户
            // if (!handlers.length && EWorkflowStepUserType.ProjectCreator === handlerInfo.userType) {
            //     handlers = [{ user_id: currentUser.user_id, name: currentUser.name }];
            // }
            break;
          case EWorkflowStepHandlerFromTarget.Audit:
            // 过程调度审批，流程提交人应带流程责任人
            if (EWorkflowType.ProcessSchedule === workflow.type && EWorkflowStepUserType.WorkflowSubmitUser === handlerInfo.userType) {
              const personLiableHandlerInfo = _.find(WORKFLOW_STEP_DEFAULT_HANDLER_INFOS, { userType: EWorkflowStepUserType._WorkflowPersonLiable });
              handlers = getHandlerPersonsSync(audit, personLiableHandlerInfo.handlerId, personLiableHandlerInfo.handlerName);
              // 其他
            } else {
              handlers = getHandlerPersonsSync(audit, handlerInfo.handlerId, handlerInfo.handlerName);
              // 流程提交人：为空时（流程尚未提交），带出当前用户
              if (!handlers.length && EWorkflowStepUserType.WorkflowSubmitUser === handlerInfo.userType) {
                handlers = [{ user_id: currentUser.user_id, name: currentUser.name }];
              }
            }
            break;
          case EWorkflowStepHandlerFromTarget.Schedule:
            const schedule: IScheduleInfo = audit?.data?.schedule_info;
            if (schedule) {
              handlers = getHandlerPersonsSync(schedule, handlerInfo.handlerId, handlerInfo.handlerName);
            }
            break;
        }
        // 允许增删审批人，则追加；不允许，则全部替换
        step.persons = step.enable_delete_person
          ? _.concat(step.persons || [], handlers)
          : handlers;
        step.persons = _.uniqBy(step.persons, 'user_id');
        // 最多人数限制
        if (step.person_limit_max && step.persons.length > step.person_limit_max) {
          step.persons = _.slice(step.persons, 0, step.person_limit_max);
        }
      });
      return workflow;
    });
  },

  // 用户=>工作流节点审批人 IUser[] => IHandlerPerson[]
  users2WorkflowStepHandlePersonsSync(users: IUser[]): IHandlerPerson[] {
    const handlerPersons: IHandlerPerson[] = [];
    _.forEach(users, user => {
      handlerPersons.push({
        user_id: user.user_id,
        name: user.name
      })
    });
    return handlerPersons;
  },

  // 添加工作流节点（'before' | 'after' ）
  addWorkflowStepSync(workflowStepAddData: IWorkflowStepAddData, oldSteps: IWorkflowStep[]): IWorkflowStep[] {
    let insertIndex = _.findIndex(oldSteps, { rank: workflowStepAddData.step.rank });
    if (-1 === insertIndex) {
      return;
    }
    if ('after' === workflowStepAddData.location) {
      insertIndex += 1;
    }
    const steps = _.cloneDeep(oldSteps);
    const newStep = this.generateWorkflowStepSync(oldSteps);
    steps.splice(insertIndex, 0, newStep);
    return this.assignStepsRankSync(steps);
  },

  // 删除工作流节点
  deleteWorkflowStepSync(step: IWorkflowStep, oldSteps: IWorkflowStep[]): IWorkflowStep[] {
    const steps = _.reject(oldSteps, { rank: step.rank });
    return this.assignStepsRankSync(steps);
  },

  // 更新工作流节点
  updateWorkflowStepSync(step: IWorkflowStep, oldSteps: IWorkflowStep[]): IWorkflowStep[] {
    const index = _.findIndex(oldSteps, { rank: step.rank });
    const steps = _.cloneDeep(oldSteps);
    steps.splice(index, 1, step);
    return this.assignStepsRankSync(steps);
  },

    // 给工作流节点分配rank
    /*private*/ assignStepsRankSync(steps: IWorkflowStep[]): IWorkflowStep[] {
    steps = _.cloneDeep(steps);
    _.forEach(steps, (step, index) => {
      step.rank = index;
    });
    return steps;
  },

    // 生成新工作流节点
    /*private*/ generateWorkflowStepSync(oldSteps: IWorkflowStep[]): IWorkflowStep {
    const stepNames = _.map(oldSteps, 'name');
    const name = '审核';
    let index = 1;
    while (-1 !== _.indexOf(stepNames, name + index)) {
      index++;
    }
    return {
      name: name + index,
      persons: [],
      person_limit: 1,
      person_limit_max: WORKFLOW_STEP_PERSON_LIMIT_INFINITE,
      type: EWorkflowStepType.Normal,
      enable_delete_step: true,
      enable_delete_person: true,
      disable_add_person: false,
      rank: 0
    };
  },

  //#region 验证
  verifyWorkflowSync(steps: IWorkflowStep[]): boolean {
    return this.verifyStepsNamesNotEmptySync(steps)
      && this.verifyStepsNamesNotDuplicateSync(steps)
      && this.verifyStepsPersonsNotEmptySync(steps)
      && this.verifyStepsPersonsNotLessThanPersonLimitSync(steps)
      && this.verifyStepsPersonsNotMoreThanPersonLimitMaxSync(steps);
  },

  // 单子暂存时验证工作流
  verifySaveWorkflowSync(steps: IWorkflowStep[]): boolean {
    return this.verifyStepsNamesNotEmptySync(steps)
      && this.verifyStepsNamesNotDuplicateSync(steps);
  },

    /* private */ verifyStepsNamesNotEmptySync(steps: IWorkflowStep[]): boolean {
    let emptyIndex = -1;
    const valid = _.every(steps, (step, index) => {
      const isValid = !!(step.name && step.name.trim());
      if (!isValid) {
        emptyIndex = index + 1;
      }
      return isValid;
    });
    if (!valid) {
      toasterService.show({ type: 'danger', title: '流程节点信息错误', contents: [`第 ${emptyIndex} 个流程节点必须填写标题`] });
    }
    return valid;
  },

    /* private */ verifyStepsNamesNotDuplicateSync(steps: IWorkflowStep[]): boolean {
    const uniqSteps = _.uniq(_.map(steps, 'name'));
    const valid = steps.length && uniqSteps.length === steps.length
    if (!valid) {
      toasterService.show({ type: 'danger', title: '流程节点信息错误', contents: ['流程节点标题不能重复'] });
    }
    return valid;
  },
     /* private */ verifyStepsPersonsNotEmptySync(steps: IWorkflowStep[]): boolean {
    let emptyIndex = -1;
    steps = _.filter(steps, { type: EWorkflowStepType.Normal });
    const valid = _.every(steps, (step, index) => {
      const isValid = step.persons.length > 0;
      if (!isValid) {
        emptyIndex = index + 2;
      }
      return isValid;
    });
    if (!valid) {
      toasterService.show({ type: 'danger', title: '流程节点信息错误', contents: [`第 ${emptyIndex} 个流程节点必须添加审批人`], timeout: 600000 });
    }
    return valid;
  },
    /* private */ verifyStepsPersonsNotLessThanPersonLimitSync(steps: IWorkflowStep[]): boolean {
    steps = _.filter(steps, { type: EWorkflowStepType.Normal });
    const invalidItem = _.find(steps, step => step.persons.length < step.person_limit);
    if (invalidItem) {
      toasterService.show({ type: 'danger', title: '流程节点信息错误', contents: [`『${invalidItem.name}』节点最少需 ${invalidItem.person_limit} 个审批人`], timeout: 600000 });
      return false;
    }
    return true;
  },
    /* private */ verifyStepsPersonsNotMoreThanPersonLimitMaxSync(steps: IWorkflowStep[]): boolean {
    steps = _.filter(steps, { type: EWorkflowStepType.Normal });
    const invalidItem = _.find(steps, step => {
      return step.person_limit_max && step.persons.length > step.person_limit_max;
    });
    if (invalidItem) {
      toasterService.show({ type: 'danger', title: '流程节点信息错误', contents: [`『${invalidItem.name}』节点最多 ${invalidItem.person_limit_max} 个审批人`], timeout: 600000 });
      return false;
    }
    return true;
  }
  //#endregion
}